import React, { useState } from 'react';
import { Input, Form, Button } from 'reactstrap';
import FontAwesomeButton from '../../../../misc/FontAwesomeButton';
import ModalComponent from '../../../../misc/ModalComponent';
import { GridListItem, GridListHeader } from '../../../../misc/GridListComponents';
import { useTranslation } from 'react-i18next';
import { OnChangeCallback } from '../../../../../util/UtilityTypes';


const filterEquals = (a: string, b: string) => a === b;

interface SchoolSoftFilterInputProps {
    filters: string[]
    onChange: OnChangeCallback<string[]>
    disabled: boolean
    name: string
};


const SchoolSoftFilterInput = (props: SchoolSoftFilterInputProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<string[]>(props.filters);
    const [currentIndex, setCurrentIndex] = useState<null | number>(null);
    const [currentFilter, setCurrentFilter] = useState<string>('');


    const submitFilter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const updatedFilters = calculateUpdatedFilters();
        if (updatedFilters) {
            setFilters(updatedFilters);
            notifyChange(updatedFilters);
        }
        setCurrentIndex(null);
        setCurrentFilter('');
    };

    const calculateUpdatedFilters = () => {
        if (!currentFilter) {
            return null
        };
        if (Number.isFinite(currentIndex)) {
            return filters.map((x, i) => i === currentIndex ? currentFilter : x);
        } else if (!filters.some(x => filterEquals(x, currentFilter))) {
            return [...filters, currentFilter];
        } else {
            return null;
        }
    }

    const editFilter = (index: number | null) => {
        if (index !== null) {
            setCurrentIndex(index);
            setCurrentFilter(filters[index]);
        };
    };

    const removeFilter = (index: number | null) => {
        if (index !== null) {
            setIsOpen(true);
            setCurrentFilter(filters[index]);
            setCurrentIndex(index);
        };
    };

    const renderModal = () => {
        return (
            <ModalComponent
                isOpen={isOpen}
                header={t('filterInput.remove.modalHeader', { ns: 'configuration' })}
                disabled={props.disabled}
                confirm={t('yes', { ns: 'common' })}
                cancel={t('no', { ns: 'common' })}
                toggleModal={toggleModal}
                update={updateFilter}
            >
                <div>
                    <p>{t('filterInput.remove.modalConfirmationText', { deleteFilter: currentFilter, ns: 'configuration' })}</p>
                    <p>{t('filterInput.remove.modalDescriptionText', { ns: 'configuration' })}</p>
                </div>
            </ModalComponent>
        );
    };

    const abort = () => {
        setCurrentIndex(null);
        setCurrentFilter('');
    };

    const toggleModal = () => {
        setIsOpen(false);
        setCurrentFilter('');
        setCurrentIndex(null);
    };

    const updateFilter = () => {
        const updatedFilter = filters.filter((_, i) => i !== currentIndex);
        setFilters(updatedFilter);
        notifyChange(updatedFilter);
        toggleModal();
    };

    const valid = () => {
        if (!currentFilter.length) {
            return false;
        };
        return true;
    };

    const notifyChange = (filters: string[]) => {
        props.onChange(props.name, filters);
    }

    const updateValue: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        setCurrentFilter(e.target.value);
    };

    const renderRow = (index: number | null, filter: string | undefined) => {
        const editing = Number.isFinite(currentIndex);
        if (index === currentIndex) {
            return (<React.Fragment key={index}>
                <GridListItem>
                    <Input type="text" disabled={props.disabled} placeholder={t('filterInput.placeHolder', { ns: 'configuration' })} autoComplete="off" value={currentFilter} onChange={updateValue} />
                </GridListItem>
                {editing ? <GridListItem span={{ startColumn: 2, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled || !valid()}>{t('action.save', { ns: 'common' })}</Button>
                    <Button style={{ width: '100%' }} color="danger" onClick={() => abort()}>{t('action.cancel', { ns: 'common' })}</Button>
                </GridListItem> : <GridListItem span={{ startColumn: 2, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled}>{t('action.add', { ns: 'common' })}</Button>
                </GridListItem>}
            </React.Fragment>)
        }
        else if (Number.isFinite(index)) {
            return (<React.Fragment key={index}>
                <GridListItem>{filter}</GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="pen" onClick={() => editFilter(index)} disabled={props.disabled || editing} title={t('filterInput.edit.title', { ns: 'configuration' })} />
                </GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="trash" onClick={() => removeFilter(index)} disabled={props.disabled || editing} title={t('filterInput.remove.title', { ns: 'configuration' })} />
                </GridListItem>
            </React.Fragment>);
        }
        else {
            return null;
        }
    };

    return (
        <div>
            <Form className="gridList" style={{ gridTemplateColumns: '16fr 1.5fr 1.5fr' }} onSubmit={(e) => submitFilter(e)}>
                <GridListHeader>{t('filterInput.listHeader', { ns: 'configuration' })}</GridListHeader>
                <GridListHeader span={{ startColumn: 2, columnCount: 2 }} />
                {filters.map((filter, index) => renderRow(index, filter))}
                {renderRow(null, currentFilter)}
                {isOpen && renderModal()}
            </Form>
        </div>);

}

export default SchoolSoftFilterInput;
